import CurrencyFormatter from "currency-formatter"
import GeneralHelper from "../../utils/GeneralHelper";

export class SellingExpenses {
  /**
   *
   * @param {number} brokerageFees
   * @param {number} filingsAndRecordings
   * @param {number} attorneyFees
   * @param {number} accountantFees
   * @param {number} escrowFees
   * @param {array<CustomField>} customFields
   */
  constructor({
    brokerageFees,
    filingsAndRecordings,
    attorneyFees,
    accountantFees,
    escrowFees,
    customFields = []
  }) {
    this.brokerageFees = brokerageFees
    this.filingsAndRecordings = filingsAndRecordings
    this.attorneyFees = attorneyFees
    this.accountantFees = accountantFees
    this.escrowFees = escrowFees
    this.customFields = customFields
  }

  get brokerageFeesFloat() {
    return CurrencyFormatter.unformat(this.brokerageFees, { code: "USD" })
  }

  get filingsAndRecordingsFloat() {
    return CurrencyFormatter.unformat(this.filingsAndRecordings, { code: "USD" })
  }

  get attorneyFeesFloat() {
    return CurrencyFormatter.unformat(this.attorneyFees, { code: "USD" })
  }

  get accountantFeesFloat() {
    return CurrencyFormatter.unformat(this.accountantFees, { code: "USD" })
  }

  get escrowFeesFloat() {
    return CurrencyFormatter.unformat(this.escrowFees, { code: "USD" })
  }

  get totalCustomFieldsAmount() {
    return GeneralHelper.sumObjectAttributeFromArray(this.customFields, "value")
  }

  get totalSellingExpenses() {
    return (
      this.brokerageFeesFloat
      + this.filingsAndRecordingsFloat
      + this.attorneyFeesFloat
      + this.accountantFeesFloat
      + this.escrowFeesFloat
      + this.totalCustomFieldsAmount
    )
  }

  get totalSellingExpensesFormatted() {
    return CurrencyFormatter.format(this.totalSellingExpenses, { code: "USD", symbol: "$" })
  }
}

export class AssetAccountsPayable {
  /**
   * @param {number} payrollPayable
   * @param {number} salesTaxPayable
   * @param {array<CustomField>} customFields
   */
  constructor({
    liabilities = [],
    payrollPayables,
    salesTaxPayables,
    customFields = []
  }) {
    this.liabilities = liabilities
    this.payrollPayables = payrollPayables
    this.salesTaxPayables = salesTaxPayables
    this.customFields = customFields
  }

  get totalLiabilities() {
    return GeneralHelper.sumObjectAttributeFromArray(this.liabilities, "amount")
  }

  get salesTaxPayablesFloat() {
    return CurrencyFormatter.unformat(this.salesTaxPayables, { code: "USD" })
  }

  get payrollPayablesFloat() {
    return CurrencyFormatter.unformat(this.payrollPayables, { code: "USD" })
  }

  get totalCustomFieldsAmount() {
    return GeneralHelper.sumObjectAttributeFromArray(this.customFields, "value")
  }

  get totalAccountsPayable() {
    return (
      this.salesTaxPayablesFloat + this.payrollPayablesFloat + this.totalCustomFieldsAmount
    )
  }

  get totalAccountsPayableFormatted() {
    return CurrencyFormatter.format(this.totalAccountsPayable, { code: "USD", symbol: "$" })
  }
  
  //original code which las liabilities instead of two fixed items
  // get totalAccountsPayable() {
  //   return this.totalLiabilities + this.totalCustomFieldsAmount
  // }

  // get totalAccountsPayableFormatted() {
  //   return CurrencyFormatter.format(this.totalAccountsPayable, { code: "USD", symbol: "$" })
  // }

  // get hasItems() {
  //   return this.liabilities.length > 0 || this.customFields.length > 0
  // }
}

export class OtherExpensesNeededToClose {
  /**
   *
   * @param {number} equipmentRepair
   * @param {number} equipmentReplace
   * @param {number} complianceWithGovernmentalAgencies
   * @param {array<CustomField>} customFields
   */
  constructor({
    equipmentRepair = 0,
    equipmentReplace = 0,
    complianceWithGovernmentalAgencies = 0,
    customFields = []
  }) {
    this.equipmentRepair = equipmentRepair
    this.equipmentReplace = equipmentReplace
    this.complianceWithGovernmentalAgencies = complianceWithGovernmentalAgencies
    this.customFields = customFields
  }

  get equipmentRepairFloat() {
    return CurrencyFormatter.unformat(this.equipmentRepair, { code: "USD" })
  }

  get equipmentReplaceFloat() {
    return CurrencyFormatter.unformat(this.equipmentReplace, { code: "USD" })
  }

  get equipmentComplianceWithGovernmentalAgenciesFloat() {
    return CurrencyFormatter.unformat(this.complianceWithGovernmentalAgencies, { code: "USD" })
  }

  get totalCustomFieldsAmount() {
    return GeneralHelper.sumObjectAttributeFromArray(this.customFields, "value")
  }

  get totalOtherExpensesNeededToClose() {
    return (
      this.equipmentRepairFloat
      + this.equipmentReplaceFloat
      + this.equipmentComplianceWithGovernmentalAgenciesFloat
      + this.totalCustomFieldsAmount
    )
  }

  get totalOtherExpensesNeededToCloseFormatted() {
    return CurrencyFormatter.format(this.totalOtherExpensesNeededToClose, { code: "USD", symbol: "$" })
  }
}

export class CurrentAssetsKeptBySeller {
  /**
   *
   * @param {array<FinanceAccount>}currentAssets
   * @param {number} returnOfLeaseDeposit
   * @param {number} pettyCash
   * @param {number} proratedRent
   * @param {number} utilityDeposit
   * @param {number} proratedTaxes
   * @param {number} rebateForPrepaidExpenses
   * @param {array<CustomField>} customFields
   */
  constructor({
    currentAssets = [],
    returnOfLeaseDeposit,
    pettyCash,
    proratedRent,
    utilityDeposit,
    proratedTaxes,
    rebateForPrepaidExpenses,
    customFields = []
  }) {
    this.currentAssets = currentAssets
    this.returnOfLeaseDeposit = returnOfLeaseDeposit
    this.pettyCash = pettyCash
    this.proratedRent = proratedRent
    this.utilityDeposit = utilityDeposit
    this.proratedTaxes = proratedTaxes
    this.rebateForPrepaidExpenses = rebateForPrepaidExpenses
    this.customFields = customFields
  }

  get returnOfLeaseDepositFloat() {
    return CurrencyFormatter.unformat(this.returnOfLeaseDeposit, { code: "USD" })
  }

  get pettyCashFloat() {
    return CurrencyFormatter.unformat(this.pettyCash, { code: "USD" })
  }

  get proratedRentFloat() {
    return CurrencyFormatter.unformat(this.proratedRent, { code: "USD" })
  }

  get utilityDepositFloat() {
    return CurrencyFormatter.unformat(this.utilityDeposit, { code: "USD" })
  }

  get proratedTaxesFloat() {
    return CurrencyFormatter.unformat(this.proratedTaxes, { code: "USD" })
  }

  get rebateForPrepaidExpensesFloat() {
    return CurrencyFormatter.unformat(this.rebateForPrepaidExpenses, { code: "USD" })
  }

  get totalCustomFieldsAmount(){
    return GeneralHelper.sumObjectAttributeFromArray(this.customFields, "value")
  }

  get totalCurrentAssetsKeptBySeller() {
    const currentAssetsSum = GeneralHelper.sumObjectAttributeFromArray(this.currentAssets, "amount")
    return (
      currentAssetsSum
      + this.returnOfLeaseDepositFloat
      + this.pettyCashFloat
      + this.proratedRentFloat
      + this.utilityDepositFloat
      + this.proratedTaxesFloat
      + this.rebateForPrepaidExpensesFloat
      + this.totalCustomFieldsAmount
    )
  }

  get totalCurrentAssetsKeptBySellerFormatted() {
    return CurrencyFormatter.format(this.totalCurrentAssetsKeptBySeller, { code: "USD", symbol: "$" })
  }
}

export class LiabilitiesAssumedByBuyer {
  /**
   * @param {array<FinanceAccount>} liabilitiesAssumedByBuyer
   * @param {number} customerPrepayments
   * @param {number} vacationPayable
   * @param {array<CustomField>} customFields
   */
  constructor({
    liabilitiesAssumedByBuyer = [],
    customerPrepayments,
    vacationPayable,
    customFields = []
  }) {
    this.liabilitiesAssumedByBuyer = liabilitiesAssumedByBuyer
    this.customerPrepayments = customerPrepayments
    this.vacationPayable = vacationPayable
    this.customFields = customFields
  }

  get customerPrepaymentsFloat() {
    return CurrencyFormatter.unformat(this.customerPrepayments, { code: "USD" })
  }

  get vacationPayableFloat() {
    return CurrencyFormatter.unformat(this.vacationPayable, { code: "USD" })
  }

  get totalCustomFieldsAmount() {
    return GeneralHelper.sumObjectAttributeFromArray(this.customFields, "value")
  }

  get totalLiabilitiesAssumedByBuyer() {
    const liabilitiesAssumedByBuyerSum = GeneralHelper.sumObjectAttributeFromArray(this.liabilitiesAssumedByBuyer, "amount")
    return (
      liabilitiesAssumedByBuyerSum
      + this.customerPrepaymentsFloat
      + this.vacationPayableFloat
      + this.totalCustomFieldsAmount
    )
  }

  get totalLiabilitiesAssumedByBuyerFormatted() {
    return CurrencyFormatter.format(this.totalLiabilitiesAssumedByBuyer, { code: "USD", symbol: "$" })
  }
}

export class LongTermDebtNotAssumedByPurchaser {
  constructor({
    longTermDebts = [],
    customFields = []
  }) {
    this.longTermDebts = longTermDebts
    this.customFields = customFields
  }

  get totalLongTermDebtNotAssumedByPurchaser() {
    return (
      GeneralHelper.sumObjectAttributeFromArray(this.longTermDebts, "amount")
      + GeneralHelper.sumObjectAttributeFromArray(this.customFields, "value")
    )
  }

  get totalLongTermDebtNotAssumedByPurchaserFormatted() {
    return CurrencyFormatter.format(this.totalLongTermDebtNotAssumedByPurchaser, { code: "USD", symbol: "$" })
  }

  get hasItems() {
   return this.longTermDebts.length > 0 || this.customFields.length > 0
  }
}

export class LongTermAssetKeptBySeller {
  constructor({
    longTermAssets = [],
    customFields = []
  }) {
    this.longTermAssets = longTermAssets
    this.customFields = customFields
  }

  get totalLongTermAssetsKeptBySeller() {
    return GeneralHelper.sumObjectAttributeFromArray(this.longTermAssets, "amount")
  }


  get totalLongTermAssetsKeptBySellerFormatted() {
    return CurrencyFormatter.format(this.totalLongTermAssetsKeptBySeller, { code: "USD", symbol: "$" })
  }

  get hasItems() {
    return this.longTermAssets?.length > 0 || this.customFields?.length > 0
  }
}

export default class AssetSaleProceedsWorksheet {
  /**
   *
   * @param {number} cashDownPayment
   * @param {SellingExpenses} sellingExpenses
   * @param {array<FinanceAccount>} accountsPayable
   * @param {array<FinanceAccount>} longTermDebtsNotAssumedByPurchaser
   * @param {OtherExpensesNeededToClose} otherExpensesNeededToClose
   * @param {CurrentAssetsKeptBySeller} totalCurrentAssetsKeptBySeller
   * @param {number} totalSellingPrice
   * @param {LiabilitiesAssumedByBuyer} existingLiabilitiesAssumedByBuyer
   * @param {array<FinanceAccount>} longTermAssetsKeptBySeller
   * @param {number} excessBalanceSheetAssets
   * @param {CustomField} customFieldToAdd
   * @param {CustomField} customFieldToSubtract
   */
  constructor({
    cashDownPayment,
    sellingExpenses,
    accountsPayable= [],
    longTermDebtsNotAssumedByPurchaser = [],
    otherExpensesNeededToClose,
    totalCurrentAssetsKeptBySeller,
    totalSellingPrice,
    existingLiabilitiesAssumedByBuyer,
    longTermAssetsKeptBySeller = [],
    excessBalanceSheetAssets,
    totalInterest,
    interestRate,
    monthlyPayments,
    amountToPayAfterCashDownPayment,
    customFieldToAdd,
    customFieldToSubtract
  }) {
    this.cashDownPayment = cashDownPayment
    this.sellingExpenses = sellingExpenses
    this.accountsPayable = accountsPayable
    this.longTermDebtsNotAssumedByPurchaser = longTermDebtsNotAssumedByPurchaser
    this.otherExpensesNeededToClose = otherExpensesNeededToClose
    this.totalCurrentAssetsKeptBySeller = totalCurrentAssetsKeptBySeller
    this.totalSellingPrice = totalSellingPrice
    this.existingLiabilitiesAssumedByBuyer = existingLiabilitiesAssumedByBuyer
    this.longTermAssetsKeptBySeller = longTermAssetsKeptBySeller
    this.excessBalanceSheetAssets = excessBalanceSheetAssets
    this.totalInterest = totalInterest
    this.interestRate = interestRate
    this.monthlyPayments = monthlyPayments
    this.amountToPayAfterCashDownPayment = amountToPayAfterCashDownPayment
    this.customFieldToAdd = customFieldToAdd
    this.customFieldToSubtract = customFieldToSubtract
  }

  // Float values

  get totalSalesAndOtherExpenses() {
    return (
      this.sellingExpenses.totalSellingExpenses
      + this.totalAccountsPayable
      + this.longTermDebtsNotAssumedByPurchaser.totalLongTermDebtNotAssumedByPurchaser
      + this.otherExpensesNeededToClose.totalOtherExpensesNeededToClose
    )
  }

  get netCashAfterSellingAndExpenses() {
    return this.cashDownPayment - this.totalSalesAndOtherExpenses
  }

  get estimatedTotalNetCashProceedsToSeller() {
    return (
      this.cashDownPayment
      - this.totalSalesAndOtherExpenses
      + this.totalCurrentAssetsKeptBySeller.totalCurrentAssetsKeptBySeller
    )
  }

  get balanceDueSeller() {
    return (
      this.totalSellingPrice
      - (this.cashDownPayment + this.existingLiabilitiesAssumedByBuyer.totalLiabilitiesAssumedByBuyer)
    )
  }

  get valueOfTotalAssetsAtClosingForSeller() {
    return this.balanceDueSeller + this.longTermAssetsKeptBySeller?.totalLongTermAssetsKeptBySeller + this.excessBalanceSheetAssets
  }

  get totalAccountsPayable() {
    return this.accountsPayable?.totalAccountsPayable
  }
  // Formatted

  get cashDownPaymentFormatted() {
    return CurrencyFormatter.format(this.cashDownPayment, { code: "USD", symbol: "$" })
  }

  get totalSalesAndOtherExpensesFormatted() {
    return CurrencyFormatter.format(this.totalSalesAndOtherExpenses, { code: "USD", symbol: "$" })
  }

  get netCashAfterSellingAndExpensesFormatted() {
    return CurrencyFormatter.format(this.netCashAfterSellingAndExpenses, { code: "USD", symbol: "$" })
  }

  get estimatedTotalNetCashProceedsToSellerFormatted() {
    return CurrencyFormatter.format(this.estimatedTotalNetCashProceedsToSeller, { code: "USD", symbol: "$" })
  }

  get excessBalanceSheetAssetsFormatted() {
    return CurrencyFormatter.format(this.excessBalanceSheetAssets, { code: "USD", symbol: "$" })
  }

  get balanceDueSellerFormatted() {
    return CurrencyFormatter.format(this.balanceDueSeller, { code: "USD", symbol: "$" })
  }

  get valueOfTotalAssetsAtClosingForSellerFormatted() {
    return CurrencyFormatter.format(this.valueOfTotalAssetsAtClosingForSeller, { code: "USD", symbol: "$" })
  }

  get totalSellingPriceFormatted() {
    return CurrencyFormatter.format(this.totalSellingPrice, { code: "USD", symbol: "$" })
  }

  get remainingBalanceOfPurchasePrice() {
    return this.totalSellingPrice - this.cashDownPayment
  }

  get totalMoneyOwnedToTheSeller() {
    return (
      this.cashDownPayment
      + this.remainingBalanceOfPurchasePrice
      - this.existingLiabilitiesAssumedByBuyer?.totalLiabilitiesAssumedByBuyer
    )
  }

  get estimatedSellerRetainedAssets() {
    return (
      this.longTermAssetsKeptBySeller?.totalLongTermAssetsKeptBySeller
      + this.totalCurrentAssetsKeptBySeller?.totalCurrentAssetsKeptBySeller
      + this.excessBalanceSheetAssets
    )
  }

  get totalInterestOnSellerCarryBackNote() {
    // Convert interest rate to decimal
    const interestRateMultiplier = this.interestRate / 100;

    // Calculate loan amount
    let loanAmount = this.amountToPayAfterCashDownPayment;

    // Calculate monthly interest rate
    const monthlyInterestRate = interestRateMultiplier / 12;

    // Calculate total interest
    let totalInterest = 0;

    for (let i = 0; i < this.monthlyPayments; i++) {
      let monthlyInterest = loanAmount * monthlyInterestRate;
      totalInterest += monthlyInterest;

      // Deduct the monthly payment from the loan amount
      loanAmount -= this.monthlyPayments;
    }

    // Return the total interest
    return totalInterest;
  }

  get grossPresentAndFutureCashAndRetainedAssetsToSeller() {
    return (
      this.totalMoneyOwnedToTheSeller
      + this.estimatedSellerRetainedAssets
      + this.totalSalesAndOtherExpenses
      + this.totalInterestOnSellerCarryBackNote
    )
  }

  get netPresentAndFutureCashAndRetainedAssetsToSeller() {
    return this.grossPresentAndFutureCashAndRetainedAssetsToSeller - this.longTermDebtsNotAssumedByPurchaser?.totalLongTermDebtNotAssumedByPurchaser
  }

  get netPresentAndFutureCashAndRetainedAssetsAfterAdjustmentsForExcess() {
    return (
      this.netPresentAndFutureCashAndRetainedAssetsToSeller
      + (this.customFieldToAdd?.value || 0)
      - (this.customFieldToSubtract?.value || 0)
    )
  }

  get proceedsSummary() {
    return {
      downPayment: this.cashDownPaymentFormatted,
      remainingBalanceOfPurchasePrice: CurrencyFormatter.format(this.remainingBalanceOfPurchasePrice, { code: "USD" }),
      totalInterestOnSellerCarryBackNote: CurrencyFormatter.format(this.totalInterestOnSellerCarryBackNote, { code: "USD" }),
      totalMoneyOwnedToTheSeller: CurrencyFormatter.format(this.totalMoneyOwnedToTheSeller, { code: "USD" }),
      estimatedSellerRetainedAssets: CurrencyFormatter.format(this.estimatedSellerRetainedAssets, { code: "USD" }),
      lessTotalExpensesTheSellerToPayAtClosing: CurrencyFormatter.format(this.totalSalesAndOtherExpenses, { code: "USD" }),
      grossPresentAndFutureCashAndRetainedAssetsToSeller: CurrencyFormatter.format(this.grossPresentAndFutureCashAndRetainedAssetsToSeller, { code: "USD" }),
      lessInterestBearingDebt: this.longTermDebtsNotAssumedByPurchaser?.totalLongTermDebtNotAssumedByPurchaserFormatted,
      netPresentAndFutureCashAndRetainedAssetsToSeller: CurrencyFormatter.format(this.netPresentAndFutureCashAndRetainedAssetsToSeller, { code: "USD" }),
      netPresentAndFutureCashAndRetainedAssetsAfterAdjustmentsForExcess: CurrencyFormatter.format(this.netPresentAndFutureCashAndRetainedAssetsAfterAdjustmentsForExcess, { code: "USD" })
    }
  }

  get sellerProceedsFromSaleAboveOrBelowTheSellingPrice() {
    let sellingPrice = this.netPresentAndFutureCashAndRetainedAssetsToSeller - this.totalSellingPrice

    if (this.customFieldToSubtract && this.customFieldToSubtract?.value) {
      sellingPrice -= this.customFieldToSubtract.value
    }

    if (this.customFieldToAdd && this.customFieldToAdd?.value) {
      sellingPrice += this.customFieldToAdd?.value
    }

    return sellingPrice
  }

  get sellerProceedsFromSaleAboveOrBelowTheSellingPriceFormatted() {
    return CurrencyFormatter.format(this.sellerProceedsFromSaleAboveOrBelowTheSellingPrice, { code: "USD" })
  }
}